// Constants + wire types shared by the client and the (Three-free) server.
// Keep this module free of any `three` import so index.ts can use it cheaply.
//
// NOTE (plan 21): there is no global WORLD_SIZE any more — each map doc owns
// its `size` (135..2160, schemas.ts MAX_MAP_SIZE). The active map's scalars
// live in src/map/active.ts; the multi-map server keeps per-map values on each
// MapRt (src/server/maps.ts).

/** WebSocket close code the server uses to evict the OLDER connection when the
 *  same character logs in again (single active session). Application close codes
 *  live in 4000–4999. The client keys on this to tell a deliberate "you opened
 *  another tab" eviction apart from a transient dropout — so it stops auto-
 *  reconnecting instead of fighting the new session (see net.ts onclose). */
export const WS_CLOSE_DISPLACED = 4001;

/** How close (xz world units) a player must stand to a world item to pick it up.
 *  The client gates the click for feel; the server re-checks it authoritatively. */
export const PICKUP_RANGE = 2.5;

// ---- Movement tuning ---------------------------------------------------------
// Ground speeds (units/sec) shared by the client sim, the server's move-speed
// budget (MAX_MOVE_SPEED in src/server/state.ts), and the loadtest bots. They
// live HERE (not config.ts) so THREE-free tools can import the real numbers;
// config.ts re-exports them for its existing consumers.
export const WALK_SPEED = 4.2;
export const RUN_SPEED = 7.6;
/** Road bonus: walking a road for >1s moves you + regens stamina this much faster. */
export const ROAD_BOOST = 1.25;

/** The server tick cadence (ms) — the setInterval in src/server/tick.ts, the
 *  overrun budget in src/server/perf.ts, and the budget line in loadtest reports. */
export const TICK_MS = 20;

/** Nearest-rank percentile over an ASCENDING pre-sorted array (works on typed
 *  arrays too). Shared by src/server/perf.ts and src/loadtest metrics/reports. */
export function percentile(sorted: ArrayLike<number>, p: number): number {
  if (!sorted.length) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

/** A point `dist` units in front of a body facing `ry`, in the game's facing
 *  convention (rotation.y = atan2(x, z) → forward = (sin, cos)). Used to place a
 *  dropped item just ahead of the player; the caller clamps to world bounds. */
export function frontOf(x: number, z: number, ry: number, dist = 1.2): { x: number; z: number } {
  return { x: x + Math.sin(ry) * dist, z: z + Math.cos(ry) * dist };
}

/** Vertical tolerance for reach checks: how far apart two FEET heights can be
 *  while still counting as "next to each other". Roomy enough for stairs, a
 *  furniture-top hop, or a low jump — but a full storey (~2.6u+), a rooftop, or
 *  a mid-air arc overhead is out of reach. */
export const REACH_Y_TOL = 2.0;

/** THE reach predicate (gravity work): horizontal range PLUS a vertical gate,
 *  so a mover directly above/below a target (another floor, mid-jump, a roof)
 *  can't attack/loot/talk/interact through the geometry. Feet heights on both
 *  sides. Every gameplay range check — server handlers and the client's feel
 *  mirrors — goes through this; don't add new bare hypot(dx,dz) gates. */
export function inReach(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  range: number,
  yTol = REACH_Y_TOL,
): boolean {
  return reachDist(ax, ay, az, bx, by, bz, yTol) <= range;
}

/** The ordering-friendly form of inReach for nearest-pick loops: XZ distance,
 *  or Infinity when the feet heights are a floor apart — so every downstream
 *  `< best` / `> range` comparison inherits the vertical gate. Same yTol rule,
 *  ONE home (the client combat/talk/interact `dist()` mirrors all delegate). */
export function reachDist(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  yTol = REACH_Y_TOL,
): number {
  return Math.abs(ay - by) > yTol ? Infinity : Math.hypot(ax - bx, az - bz);
}

// ---- Interest management (AoI) ---------------------------------------------
// The server no longer broadcasts the whole world to every socket: each client
// receives only entities inside its area of interest, at a rate tiered by
// distance (near ring every tick, mid/far rings less often). These are the
// DEFAULTS — the server may override any knob via env vars (src/server/
// interest.ts), and sends its effective values to debug clients via `imstats`.
//
// The enter/leave wire contract (consumed by net.ts):
// - ENTER  = an entity's full record appears in your `state` payload while you
//   don't know it. The server force-includes fresh entries on the next tick.
// - QUIET  = in interest but not due this tick (mid/far cadence) — the client
//   holds/interpolates. Absence from a payload does NOT mean gone.
// - LEAVE  = the id is listed in `state.leave.{players|npcs|items}`, exactly
//   once. The client despawns/hides it on receipt.
export const IM_DEFAULTS = {
  /** Uniform grid cell size (world units). */
  cellSize: 60,
  /** Ring radii (units), near < mid < far. Near must exceed the fog far plane
   *  (135, daynight.ts) so despawn pops happen inside full fog. */
  nearRadius: 150,
  midRadius: 250,
  farRadius: 350,
  /** Update cadence per ring, in ticks (tick = 20ms / 50Hz). Near defaults to
   *  every tick; IM_NEAR_EVERY=2 (25Hz) halves per-client bandwidth with no
   *  visible change — the client renders 80ms behind (INTERP_DELAY, net.ts),
   *  so 40ms-apart snapshots still bracket every render time. Keep nearEvery
   *  small enough that ≥2 snapshots fit inside INTERP_DELAY. */
  nearEvery: 1,  // 50Hz (prod runs 2 = 25Hz via IM_NEAR_EVERY)
  midEvery: 5,   // 10Hz
  farEvery: 25,  // 2Hz
  /** Hysteresis margin (units): promote to a closer ring immediately, demote /
   *  leave only past radius + margin — kills boundary flapping. The interest
   *  EXIT radius is farRadius + hysteresis. */
  hysteresis: 25,
  /** How often (ticks) each connection's interest set is recomputed. */
  refreshEvery: 5, // 10Hz, phase-staggered per connection
  /** Hard cap on entity records per state message; overflow keeps its place in
   *  the pending-enter queue, so a login in a crowd streams in over a few ticks.
   *  A ring bigger than the cap ROUND-ROBINS across ticks (per-ring rotation
   *  cursors in interest.ts) — every entity stays ≤ ceil(n/cap) ticks stale,
   *  none starve. */
  maxEntities: 128,
  /** Radius for scoped transient events (swing/projectile). 0 → exit radius. */
  eventRadius: 0,
  /** Local "say" range for chat. */
  chatRadius: 150,
  /** NPC AI sleep: skip roam/flee/combat AI (timers still run) when no player is
   *  within this radius. 0 disables sleeping. */
  npcIdleRadius: 450,
  /** imstats emission rate (Hz) for opted-in debug clients. */
  statsHz: 2,
};
export type ImConfig = typeof IM_DEFAULTS;

// ---------------------------------------------------------------------------
// Progression (Plan 09) — the shared XP / skill-box / influence engine knobs.
// XP curve thresholds live per-bar in the skill content docs (data); only the
// engine-wide caps live here. Both runtimes import this (wire-contract file).
// ---------------------------------------------------------------------------
export const PROGRESSION_DEFAULTS = {
  /** Hard ceiling on LIFETIME-GRANTED influence — the load-bearing cap. Bounds
   *  spendable points no matter how many over-provisioned mints exist.
   *  Combat alone can spend up to `capBoxes` 12; fishing/hunting/cooking still
   *  need room — ~20 means deep Combat (~9–12) leaves a few points for other
   *  skills, not a full second tree. Raise sources/cap deliberately if mastery
   *  of multiple full skills is intended. */
  globalCap: 20,
  /** Influence granted at character creation (also seeds influence_granted). */
  creationGrant: 2,
};
export type ProgressionConfig = typeof PROGRESSION_DEFAULTS;

/** Explicit removals riding the `state` message (see the contract above). */
export type StateLeave = { players?: string[]; npcs?: string[]; items?: string[]; fish?: string[] };

/** Per-phase tick-loop timing, riding ImStats when the server has data (fed by
 *  src/server/perf.ts, bracketed in tick.ts). Additive + optional so real clients
 *  ignore it — consumed by the loadtest observer (src/loadtest) for the subsystem
 *  breakdown in its reports. */
export type ImPerf = {
  /** Complete ticks aggregated in this stats window. */
  window: number;
  /** Whole-tick wall time vs the 20ms budget; overruns = ticks that blew it. */
  tick: { avgMs: number; p99Ms: number; maxMs: number; overruns: number };
  /** Phase name (see perf.ts TICK_PHASES) → per-tick avg/max ms. */
  phases: Record<string, { avgMs: number; maxMs: number }>;
};

/** Debug stats pushed (~2Hz) to clients that opted in with {t:"imstats",on:true}.
 *  `cfg` carries the server's EFFECTIVE knobs so the HUD draws env-tuned values. */
export type ImStats = {
  t: "imstats";
  conns: number;
  cfg: { cell: number; near: number; mid: number; far: number; exit: number };
  /** This client's interest set + egress over the last stats window. */
  you: { near: number; mid: number; far: number; items: number; entPerSec: number; bytesPerSec: number };
  world: { players: number; npcs: number; items: number; cellsOccupied: number; maxCellOccupancy: number };
  /** Occupied cells (players + NPCs combined): [cellX, cellZ, count]. */
  cells: [number, number, number][];
  /** Tick-loop phase timings — present only when the server has a complete window. */
  perf?: ImPerf;
};

// ---- Weather ----------------------------------------------------------------
// ONE global weather state, server-owned (src/server/weather.ts is the sim; the
// client's src/weather.ts renders it). It deliberately does NOT ride the `state`
// snapshot or the interest system: weather retargets a few times per hour, so it
// travels as a change-driven `weather` broadcast (the `broadcastProp` pattern)
// carrying a TARGET + ramp seconds — both sides ease toward the target locally,
// so the wire stays silent between changes.

export type PrecipKind = "none" | "rain" | "snow";

/** The global weather blob. Every axis is a continuous 0..1 — "sunny / partly /
 *  overcast" are just labels over the `cloud` scalar (the dev panel names the
 *  bands; the sim never needs an enum). */
export type WeatherTarget = {
  cloud: number;   // cover: 0 sunny · ~0.5 partly cloudy · 1 overcast
  precip: number;  // intensity: 0 dry · 1 downpour/blizzard
  kind: PrecipKind;// rain vs snow, picked from the season at retarget + latched while falling
  storm: number;   // lightning severity: 0 none · 1 full storm
  wind: number;    // cloud drift speed, rain slant, snow drift, deco sway speed
};

/** Clear-sky defaults (first-ever boot, and the client's pre-join state). */
export const DEFAULT_WEATHER: WeatherTarget = {
  cloud: 0.15, precip: 0, kind: "none", storm: 0, wind: 0.2,
};

/** THE weather transition: smoothstep-ease every numeric axis of `cur` from
 *  `from` toward `tgt` at progress `t` (0..1), in place — plus the kind latch
 *  (`kind` isn't lerpable: adopt the target's kind as soon as it's the one
 *  falling; while fading OUT keep the old kind so the last drops stay
 *  rain/snow, flipping to "none" only once dry). Lives here so the server sim
 *  and the client mirror share ONE definition instead of drifting copies;
 *  `t = 1` doubles as an exact snap. */
export function easeWeatherTarget(
  cur: WeatherTarget, from: WeatherTarget, tgt: WeatherTarget, t: number,
): void {
  const k = t * t * (3 - 2 * t); // smoothstep
  cur.cloud = from.cloud + (tgt.cloud - from.cloud) * k;
  cur.precip = from.precip + (tgt.precip - from.precip) * k;
  cur.storm = from.storm + (tgt.storm - from.storm) * k;
  cur.wind = from.wind + (tgt.wind - from.wind) * k;
  cur.kind = tgt.kind !== "none" ? tgt.kind : cur.precip > 0.001 ? cur.kind : "none";
}

// The world calendar, derived from a single persistent day counter that bumps
// on each tod wrap (in-game midnight). Deterministic on both sides — only `day`
// travels the wire.
export const MOON_PHASES = 8;  // 0 = new … 4 = full … back to new
export const YEAR_DAYS = 24;   // 3 full moon cycles per year
export const COLD_FROM = 16;   // days 16..23 of each year = cold season (snow)
export const moonPhase = (day: number): number =>
  ((day % MOON_PHASES) + MOON_PHASES) % MOON_PHASES;
export const isColdSeason = (day: number): boolean =>
  ((day % YEAR_DAYS) + YEAR_DAYS) % YEAR_DAYS >= COLD_FROM;

/** A character's chosen look, picked once at creation and rendered by the
 *  shared realistic-humanoid player model (content/models/player.json): each
 *  color overrides the doc's palette key of the same name; `hairStyle` picks
 *  the archetype hair build. Persisted on the characters row and ridden over
 *  the wire (like `weapon`) so late joiners see it. */
/** The closed set of hair builds both humanoid builders implement — ONE list,
 *  imported by the type below, the sanitizer, the swatch UI (config.ts), and
 *  the content schema, so a new style is a geometry edit + one entry here. */
export const HAIR_STYLES = [
  "cap", "crew", "undercut", "swept", "bun", "long", "ponytail", "mohawk", "none",
] as const;
export type HairStyle = (typeof HAIR_STYLES)[number];

/** Closed set of beard builds the humanoid builder implements — same contract
 *  as HAIR_STYLES: one enum entry + a recipe/freehand path in humanoid.ts.
 *  Geometry uses the shared jaw/chin landmarks (scalp frame extension). Default
 *  for heroes is `none`; NPCs set `body.head.beardStyle`. Face-add parts
 *  (`beard-gray`) still work for one-off custom tufts. **Player Appearance:
 *  female is always clean-shaven** (see BEARD_STYLES_BY_SEX). */
export const BEARD_STYLES = [
  "none", "short", "full", "goatee", "mustache",
] as const;
export type BeardStyle = (typeof BEARD_STYLES)[number];

/** Body type — a male/female frame preset picked once at creation. Drives the
 *  humanoid build's proportions (female = slimmer limbs, rounder hips, tapered
 *  waist, a small bust); see src/model/humanoid.ts. */
export const SEXES = ["male", "female"] as const;
export type Sex = (typeof SEXES)[number];

/** Player chargen hair options per body type. The full HAIR_STYLES set still
 *  exists for geometry + NPC docs; Appearance / UI only offer these.
 *  `long` is the shared cut. */
export const HAIR_STYLES_BY_SEX: Record<Sex, readonly HairStyle[]> = {
  male: ["cap", "crew", "undercut", "swept", "long", "mohawk", "none"],
  female: ["bun", "long", "ponytail"],
};

/** Player chargen beard options per body type. Females cannot wear a beard. */
export const BEARD_STYLES_BY_SEX: Record<Sex, readonly BeardStyle[]> = {
  male: BEARD_STYLES,
  female: ["none"],
};

export function hairStylesFor(sex: Sex): readonly HairStyle[] {
  return HAIR_STYLES_BY_SEX[sex];
}

export function beardStylesFor(sex: Sex): readonly BeardStyle[] {
  return BEARD_STYLES_BY_SEX[sex];
}

/** Keep a hair style if it's allowed for `sex`; else the body default. */
export function fitHairStyle(style: unknown, sex: Sex): HairStyle {
  const allowed = HAIR_STYLES_BY_SEX[sex] as readonly string[];
  if (typeof style === "string" && allowed.includes(style)) return style as HairStyle;
  return DEFAULT_HAIR[sex];
}

/** Keep a beard style if allowed for `sex`; females always `none`. */
export function fitBeardStyle(style: unknown, sex: Sex): BeardStyle {
  if (sex === "female") return "none";
  if (typeof style === "string" && (BEARD_STYLES as readonly string[]).includes(style)) {
    return style as BeardStyle;
  }
  return "none";
}

/** Body-build silhouette presets the humanoid archetype offers (`body.build`
 *  in a model doc) — named girth/width/leg-length multiplier sets over the one
 *  shared frame, so a goblin reads squat and a king stout without per-doc
 *  sculpting. ONE list, imported by the content schema and the builder; the
 *  preset numbers live in src/model/humanoid.ts BUILD_PRESETS. Composes with
 *  `sex` (the female frame slims over whichever build is picked). */
export const BODY_BUILDS = ["average", "stout", "slight", "squat"] as const;
export type BodyBuild = (typeof BODY_BUILDS)[number];

/** Facial expressions the humanoid archetype's face renders (`body.head.
 *  expression` in a model doc). ONE list, imported by the builder, the content
 *  schema, and the studio switcher; "happy" is the relaxed default and the
 *  byte-identical historical face, "talking" animates the mouth open/closed.
 *  The per-expression brow/eye/mouth geometry lives in src/model/face.ts. */
export const FACE_EXPRESSIONS = ["happy", "sad", "angry", "surprised", "annoyed", "talking"] as const;
export type FaceExpression = (typeof FACE_EXPRESSIONS)[number];

export type Appearance = {
  shirt: number;
  pants: number;
  skin: number;
  hair: number;
  eye: number;
  hairStyle: HairStyle;
  /** Beard build (BEARD_STYLES). Player beards use the hair color. */
  beardStyle: BeardStyle;
  sex: Sex;
};

/** The classic hero colors — also the fallback for anything unspecified. */
export const DEFAULT_APPEARANCE: Appearance = {
  shirt: 0x3b6ea5,
  pants: 0x355070, // player.json's "trousers" palette default
  skin: 0xe0ac69,
  hair: 0x4a2f1a,
  eye: 0x222028,
  hairStyle: "cap",
  beardStyle: "none",
  sex: "male",
};

/** Default hair per body type — female starts with long hair, male with the
 *  short crop. Used when a style isn't allowed for the body (`fitHairStyle`). */
export const DEFAULT_HAIR: Record<Sex, HairStyle> = { male: "cap", female: "long" };

/** Coerce an untrusted wire value into a safe Appearance (join handler).
 *  Sex is resolved first so hair/beard are fit to that body's allowed sets. */
export function sanitizeAppearance(v: unknown): Appearance {
  const o = (v ?? {}) as Record<string, unknown>;
  const col = (x: unknown, fb: number) =>
    typeof x === "number" && Number.isFinite(x) ? (x >>> 0) & 0xffffff : fb;
  const sex: Sex = (SEXES as readonly string[]).includes(o.sex as string)
    ? (o.sex as Sex)
    : "male";
  return {
    shirt: col(o.shirt, DEFAULT_APPEARANCE.shirt),
    pants: col(o.pants, DEFAULT_APPEARANCE.pants),
    skin: col(o.skin, DEFAULT_APPEARANCE.skin),
    hair: col(o.hair, DEFAULT_APPEARANCE.hair),
    eye: col(o.eye, DEFAULT_APPEARANCE.eye),
    hairStyle: fitHairStyle(o.hairStyle, sex),
    beardStyle: fitBeardStyle(o.beardStyle, sex),
    sex,
  };
}

// ---- Characters -------------------------------------------------------------
/** Max characters per account — the character-select cap. The server is
 *  authoritative (it enforces the cap on create + rides `max` in the list
 *  response); the client imports this as the fallback + grid size. */
export const MAX_CHARACTERS = 3;

/** A lightweight character summary for the pre-world select screen — enough to
 *  draw the card + the 3D look preview, without hydrating inventory/quests/etc.
 *  Shared so the server query and the client screen can't drift. */
export type CharacterSummary = {
  id: string;
  name: string;
  look: Appearance;
  mapId: string;
  kills: number;
  title: string | null;
  updatedAt: string;
};

// ---- Wearables --------------------------------------------------------------
// The five body areas a humanoid can dress (an item is wearable iff its spec has
// a `wear` block naming one — same block-presence-is-the-switch pattern as
// `weapon`/`combat`). A player's equipped set is a sparse slot→ItemType record
// (`Gear`) that rides the snapshot like `weapon` and persists on the character.
export const WEAR_SLOTS = ["head", "torso", "legs", "feet", "back"] as const;
export type WearSlot = (typeof WEAR_SLOTS)[number];
export type Gear = Partial<Record<WearSlot, ItemType>>;

/** A player as it travels over the wire. `y` is the FEET height — sent
 *  explicitly so remotes appear on walkable floors/stairs, and (gravity plan)
 *  it rises through a jump/fall arc, `air` marking the flight. `ry` is the
 *  body yaw in radians. */
export type NetPlayer = {
  id: string;
  name: string;
  look: Appearance; // creation-time look (colors + sex-gated hairStyle/beardStyle)
  x: number;
  y: number;
  z: number;
  ry: number;
  moving: boolean;
  running: boolean; // moving fast (Shift held) — drives the run animation on remotes
  // Held channels leave the bag while equipped (bag↔hand, like wear) and persist
  // on characters.weapon / .offhand / .tool (migration 0020 for offhand+tool).
  weapon: ItemType | null; // main-hand weapon in remotes' hands (null = unarmed)
  offhand: ItemType | null; // offhand shield (plan 14) — off fist; 2H locks this
  tool: ItemType | null; // rod / hatchet — main-hand exclusive with weapon
  blocking: boolean; // holding the shield up (plan 14) — drives the block pose on remotes + the server's frontal soak
  fishing: boolean; // a line is out (plan 10) — drives the two-handed rod pose on remotes. Mirrors the live cast: set on `cast`, cleared by `endCast` (the single teardown choke every exit — reel/cancel/knockout/unequip/transfer/late-window — routes through)
  cx?: number; // the bobber/cast point — present ONLY while `fishing`, so remotes render the float on the water (set on `cast`, cleared by `endCast`). Static for the whole cast (fish come to it). Omitted from the wire when no line is out.
  cz?: number;
  cooking?: ItemType; // the raw on the pan (plan 25) — present ONLY while a cook session is live, so remotes render the pan + sizzling dish at the hearth. Set on an accepted `cook`, cleared by endCookSession (the single teardown choke every exit — resolve/cancel/knockout/transfer/close — routes through)
  hx?: number; // the hearth anchor the cook is standing at — present ONLY while `cooking` (the cx/cz twin)
  hz?: number;
  chopping?: boolean; // mid tree-cut session — drives hatchet ready stance + exclusive tree lock on remotes. Set on accepted chopStart, cleared by endChop
  chopSeq?: number; // increments each committed chop swing — remotes fire the hatchet attack clip on change
  fireStarting?: boolean; // mid firestart minigame — kneel + spark swings on remotes. Set on accepted fireStart, cleared by endFireStart
  fireSeq?: number; // increments each committed spark strike — remotes fire the spark swing on change
  gear: Gear; // worn wearables (server-owned) — rendered on the rig by every client
  ready: boolean; // holding a combat stance (engaged + armed) — drives the ready pose on remotes
  hp: number; // current hitpoints (server-owned as of Step 3; rides the snapshot so remotes see damage/low health)
  maxHp: number;
  down: boolean; // knocked out (server-owned) — drives the collapse/topple on remotes until revived
  fighting: boolean; // engaged with a fightable NPC — shows the overhead HP bar on remotes
  title: string | null; // equipped floating title (highest earned rank, Plan 09) — rendered over the head by every client
  pose?: string; // named body pose (content/anim/clips.json `poses` — seated/sitting/prone/…) — present ONLY while posed (the cooking/cx idiom). Drives the blended pose on remotes; TRANSIENT (never persisted — relog = standing). Server clears it on move/jump/attack/damage (breakPose)
  air?: boolean; // airborne per the server's vertical envelope (jump or real fall — gravity plan). Present ONLY while airborne (the pose idiom); drives the airborne pose + ballistic y-extrapolation on remotes, so stairs/lifts never read as flight
  /** Present-only while snared (rooted, can still act). */
  snare?: boolean;
  /** Present-only while stunned (no move, no act). */
  stun?: boolean;
  /** Present-only while slowed (move at reduced speed). */
  slow?: boolean;
};

/** An NPC as it travels over the wire. The server owns every *dynamic* NPC
 *  (anything that roams or fights) and streams this each tick; clients render it
 *  and never simulate it. `y` is the resolved feet height + walk bob (sent like
 *  NetPlayer.y so the goblin stands on the same floors it walked). `engaged` is
 *  true while ANY player is fighting it (drives the overhead HP/STA bars on every
 *  client). `atkSeq` increments once per punch the NPC throws — clients fire the
 *  lunge animation on each change. `defeated` toggles the topple (and, on its
 *  falling edge, a respawn). */
export type NetNpc = {
  id: string;
  /** Airborne (a ballistic fall — gravity plan). Present-only-while-true. */
  air?: boolean;
  x: number;
  y: number;
  z: number;
  ry: number;
  hp: number;
  maxHp: number;
  stamina: number;
  maxStamina: number;
  engaged: boolean;
  atkSeq: number;
  defeated: boolean;
  /** True while the NPC is bolting away from a player (wildlife flee/scamper).
   *  Cosmetic on the client: drives a faster, more frantic walk bob. */
  fleeing: boolean;
  /** Id of the player currently in a conversation with this NPC, or null. While
   *  set, the server halts the NPC's roam and turns it to face that player; every
   *  client sees it as "busy" (a second player can't start talking). See talk.ts. */
  talker: string | null;
  /** Id of the PLAYER this NPC is currently targeting, or null (plan 14 —
   *  mirrors the `talker` pattern). `tgt === me.id` is the auto-engage signal:
   *  the client shows the engaged presentation (bars + action bar) with no
   *  click when a territorial/assisting NPC starts the fight. NPC-vs-NPC
   *  targets ride as null — remotes don't need to know which goblin a guard
   *  is punching (atkSeq/hp already animate it). */
  tgt: string | null;
  /** Named body pose (map-spawn authored idle — a seated innkeeper). Present
   *  only while posed; the server wrapper clears it the moment the NPC moves/
   *  engages/flees (stays standing until respawn). */
  pose?: string;
  /** Present-only while snared (rooted, can still act). */
  snare?: boolean;
  /** Present-only while stunned (no move, no act). */
  stun?: boolean;
  /** Present-only while slowed (move at reduced speed). */
  slow?: boolean;
};

// ---- Wire-honest state-frame shapes (board N3, the full/slim split) ----------
// NetPlayer/NetNpc above describe the SERVER record and the ENTER fragment.
// Ring-due UPDATES are slim: fragPlayerSlim omits the immutable name/look,
// fragNpcSlim the post-spawn-static maxHp/maxStamina — the client retains those
// from the enter. `state.players`/`state.npcs` therefore carry these types, so
// client code can't read an enter-only field off an update without the compiler
// forcing the undefined check.

/** A `state.players` entry: full on enter, name/look absent on ring updates. */
export type NetPlayerWire = Omit<NetPlayer, "name" | "look"> & Partial<Pick<NetPlayer, "name" | "look">>;

/** A `state.npcs` entry: full on enter, maxHp/maxStamina absent on ring updates. */
export type NetNpcWire = Omit<NetNpc, "maxHp" | "maxStamina"> & Partial<Pick<NetNpc, "maxHp" | "maxStamina">>;

// ---- Fish (Living Waters) ---------------------------------------------------
// Server-simulated water-population fish, the 4th interest-managed entity kind.
// Deliberately minimal: no y (shadows are surface-locked — the client draws at
// WATER_LEVEL − shadowDepthOffset), no hp (fish aren't combatants); `st` is the
// behavior projection driving the shadow's cosmetic FX.

/** The fish's wire/render behavior state (sim wireState projection). */
export type FishWireState = "swim" | "dart" | "approach" | "investigate" | "nibble" | "bite" | "hooked";

export type NetFish = {
  id: string;
  /** Species — the fish ITEM type; the client resolves spec.fish for size. */
  type: ItemType;
  x: number;
  z: number;
  ry: number;
  st: FishWireState;
};

// ---- Quests ---------------------------------------------------------------
// Quest *definitions* (steps, objectives, all text) live in src/quest/ and are shared
// by both sides. Only a player's PROGRESS travels the wire, and only to that one player
// (a personal `{ t:"quests", quests, kills }` message — never the broadcast snapshot,
// since quest state is private). `stepIndex` is the current active step; it equals the
// quest's step count once every step is done (`status:"completed"`).
export type NetQuest = {
  id: string;
  stepIndex: number;
  status: "active" | "completed";
};

/** Owner-only per-skill progress (Plan 09). `xp` is a small bar→value map (Combat =
 *  {melee, ranged}); `boxes` = unlocked box ids. Mirrors CharSkillRow server-side. */
export type NetSkill = {
  skill: string;
  xp: Record<string, number>;
  boxes: string[];
};

/** Owner-only per-track standing (Plan 09). Mirrors CharStandingRow server-side. */
export type NetStanding = {
  track: string;
  rank: number;
  progress: number;
};

// ---- Items ----------------------------------------------------------------
// Item *types* are a small closed set, GENERATED from content/items/*.json (run
// `bun run gen` after adding an item — src/content/item-types.ts). Re-exported
// here so wire types and both runtimes keep importing it from shared.ts.
import type { ItemType } from "./content/item-types.ts";
export type { ItemType };

/** A pick-up-able item lying in the world, as it travels over the wire. `id` is
 *  unique per instance (seeded items are stable; dropped items get a fresh id).
 *  `y` is the resting feet height the dropping client stood at. */
export type WorldItem = {
  id: string;
  type: ItemType;
  x: number;
  y: number;
  z: number;
  /** For a coin pile (type "obol"): how many obol it's worth. Picking it up credits
   *  the wallet by this amount (never the bag). Absent / 1 for ordinary items. */
  amount?: number;
};

// The items seeded on a map's first-ever boot live in its map doc's `items`
// (e.g. content/maps/overworld.json — the server's first-boot seeding reads
// them per map in src/server/persist.ts).

/** A deployed fishing net (plan 10) as it travels the wire. Server-owned +
 *  durable (deployed_nets, migration 0010); everyone sees the floats, but only
 *  the owner may collect. `readyAt` is ABSOLUTE epoch ms (the soak counts
 *  through downtime, like NPC respawns); `mine` is stamped per-recipient when
 *  the server serializes (ownership is a character id the client never sees). */
export type NetDeploy = {
  id: string;
  x: number;
  z: number;
  /** Epoch ms the soak completes (ready to haul). */
  readyAt: number;
  tier: number;
  /** True on the receiving client's own nets (drives the Collect slot). */
  mine?: boolean;
};

/** A deployed hunting trap (plan 11) as it travels the wire — the NetDeploy
 *  shape on land. Server-owned + durable (deployed_nets with kind="trap",
 *  migration 0011); everyone sees the snare, only the owner may collect. */
export type TrapDeploy = {
  id: string;
  x: number;
  z: number;
  /** Epoch ms the soak completes (ready to collect). */
  readyAt: number;
  tier: number;
  /** True on the receiving client's own traps (drives the Collect slot). */
  mine?: boolean;
};

/** A deployed fermenter (plan 18 CP3, Brewing branch) as it travels the wire —
 *  the TrapDeploy shape (deployed_nets with kind="brew", no new migration —
 *  migration 0011 already generalized the table). Everyone sees the cask, only
 *  the owner may collect; `readyAt` is absolute epoch ms, `mine` per-recipient. */
export type BrewDeploy = {
  id: string;
  x: number;
  z: number;
  /** Epoch ms the soak completes (ready to collect). */
  readyAt: number;
  tier: number;
  /** True on the receiving client's own fermenters (drives the Collect slot). */
  mine?: boolean;
};

/** A burning player-started campfire (plan 25) as it travels the wire — the
 *  BrewDeploy shape with readyAt read as BURN-OUT time (deployed_nets with
 *  kind="fire", no new migration). Everyone sees it and can cook at it; the
 *  server sweeps it away when it burns out (nothing is collected). */
export type FireDeploy = {
  id: string;
  x: number;
  z: number;
  /** Epoch ms the fire burns out (the server removes it then). */
  readyAt: number;
  /** True on the receiving client's own fires (feeds the fire-cap messaging). */
  mine?: boolean;
};

// ---- Wire protocol ---------------------------------------------------------
// The full WebSocket message contract. `ClientMsg` = every intent the client
// sends — DEFINED as zod schemas in src/wire.ts and re-exported below (net.ts
// `send()` is typed to it, so an ill-formed intent fails to compile; the server
// safeParses every inbound frame against the same schemas, so shape validation
// can't drift from the type). `ServerMsg` = every message the server sends
// (net.ts's handler switches on it; server code builds frames via `packMsg`
// below so a drifted field fails to compile).
//
// IMPORTANT: the SERVER still treats incoming messages as UNTRUSTED. The wire
// schemas guarantee SHAPE only (tolerantly coerced, never trusted); ownership,
// range, rate, and content-existence checks live in the handlers as always.
// Don't remove a handler-side semantic check because "the type says so".

/** One inventory stack as it rides the wire (mirrors the server's CharInvSlot,
 *  which is server-only and can't be imported here). */
export type WireInvSlot = { slot: number; type: ItemType; count: number };

/** Swing SELECTOR relayed on the `swing` event: `"primary"` = the sender's
 *  equipped-weapon (or unarmed) attack, resolved per-rig on the receiver; any
 *  other value is a shared move-clip key (content/moves). A short string so the
 *  wire stays weapon-agnostic. Must match `AttackSel` in src/anim/pose.ts. */
export type WireAttackStyle = "primary" | (string & {});

/** Client → server intents — DERIVED from the zod schemas in src/wire.ts
 *  (board Q1): `z.infer` is this type and `safeParse` is the server's runtime
 *  guard, so the compile-time contract and the wire validation can never
 *  drift. Per-variant docs + the tolerance rule live next to the schemas.
 *  This is a TYPE-ONLY re-export (erased at build) — the client bundle and the
 *  loadtest bots do not pull zod through it. */
export type { ClientMsg } from "./wire.ts";

/** Server → client messages. */
export type ServerMsg =
  // `dev` = WEATHER_DEV=1 (F4 panel). `devCommands` = DEV_COMMANDS=1 (journal
  // quest scrubber, /map, /give, F8 grant panel, …). Sparse — omit when off.
  | { t: "welcome"; id: string; dev?: boolean; devCommands?: boolean }
  | { t: "needCharacter" }
  | {
      // `map` = the map doc id this character is on (plan 21) — the client
      // resolves the full doc from its bundled manifest and builds THAT world.
      t: "spawn"; map: string; x: number; y: number; z: number; ry: number;
      name: string; look: Appearance; hp: number; maxHp: number;
      /** Attack-gate pool after skill-box bonuses (applyBoxStats). Optional for
       *  older servers; client falls back to the base PLAYER_STA_MAX. */
      stamina?: number;
      maxStamina?: number;
      weapon: ItemType | null;
      /** Offhand shield / held tool — same bag-exit rule as weapon (migration 0020). */
      offhand?: ItemType | null;
      tool?: ItemType | null;
      gear: Gear; inventory: WireInvSlot[]; obol: number;
      /** First-login cutscene cue (plan 26): play this cinematic doc before
       *  revealing the world; the client acks via `cinematicDone`. */
      cinematic?: string;
    }
  // Interest-managed snapshot (see IM_DEFAULTS): absence ≠ gone; removal is `leave`.
  // Entries are the WIRE shapes (full on enter, slim on ring updates — see
  // NetPlayerWire/NetNpcWire above), not the full server records.
  | {
      t: "state"; ts: number; tod: number;
      players: NetPlayerWire[]; npcs: NetNpcWire[]; items?: WorldItem[]; fish?: NetFish[]; leave?: StateLeave;
    }
  // `stag` (seconds) rides only while a block-stagger is live (plan 14).
  // `snare`/`stun`/`slow` (seconds remaining) ride only while those CC timers are live.
  // `slowMul` rides with `slow` so the client predicts speed correctly.
  // `stamina`/`maxStamina` mirror the server attack-gate pool (skill-box bonuses
  // layer onto PLAYER_STA_MAX via applyBoxStats). Current `stamina` is still
  // predicted client-side between pushes; the client adopts a raised max and
  // tops up current the same way HP does.
  | {
      t: "vitals"; hp: number; maxHp: number; down: boolean;
      stamina?: number; maxStamina?: number;
      stag?: number; snare?: number; stun?: number; slow?: number; slowMul?: number;
    }
  // `ry` rides only when the snap owns the facing too (the seat snap — `sit`).
  | { t: "correction"; x: number; y: number; z: number; ry?: number }
  // Owner-only body-pose echo (the vitals pattern): the snapshot never carries
  // your OWN player record, so sit snaps, emote confirms, and breakPose stands
  // all confirm through this. `id` = pose id, null = standing.
  | { t: "pose"; id: string | null }
  // Your character moved to another map (plan 21 — the /map command, portals
  // later). v1 client reaction: reload; the login flow rebuilds the new world.
  | { t: "transfer"; map: string; x: number; y: number; z: number; ry: number } // in-session map swap: arrival pose rides along (plan 24)
  | { t: "inventory"; bag: WireInvSlot[] }
  | { t: "wallet"; obol: number }
  | { t: "gear"; gear: Gear }
  | { t: "quests"; quests: NetQuest[]; kills: number }
  | { t: "skills"; skills: NetSkill[] }
  | { t: "influence"; influence: number }
  | { t: "standing"; standing: NetStanding[]; title: string | null }
  | { t: "chat"; id: string; name: string; text: string }
  | { t: "swing"; id: string; style: WireAttackStyle }
  /** Onset combat VFX for snare / stun / knockback (near-broadcast). Snapshot
   *  flags (`snare`/`stun` on NetPlayer/NetNpc) sustain the status icons. */
  | { t: "combatFx"; kind: "snare" | "stun" | "slow" | "knockback"; target: "player" | "npc"; id: string; x: number; y: number; z: number }
  /** Special-move AoE shape telegraph (near-broadcast). Sector = cone/circle; line = pierce corridor. */
  | { t: "aoe"; id: string; kind: "sector"; x: number; y: number; z: number; ry: number; arcDeg: number; range: number }
  | { t: "aoe"; id: string; kind: "line"; x: number; y: number; z: number; ry: number; length: number; width: number }
  /** Flying arrow (near-broadcast). `to*` is the cosmetic endpoint (nudged wide
   *  on a miss). `hit` + `at*` let every nearby client spawn a Miss float at the
   *  intended target on arrival — hits still use the HP-drop damage number. */
  | { t: "projectile"; fromX: number; fromY: number; fromZ: number; toX: number; toY: number; toZ: number; speed: number; hit: boolean; atX: number; atY: number; atZ: number }
  | { t: "prop"; id: string; state: "opened" | "hidden" | "shown" | "cut" }
  | { t: "props"; opened: string[]; hidden: string[]; cut?: string[] }
  // Structure doors (session-only open state; default closed on boot).
  | { t: "door"; id: string; open: boolean }
  | { t: "doors"; open: string[] }
  // Global weather (change-driven broadcast + a ramp:0 baseline on join). The
  // client eases its rendered weather toward `tgt` over `ramp` seconds; `day`
  // derives moonPhase/isColdSeason locally. NEVER moves into `state` — see the
  // Weather section above.
  | { t: "weather"; tgt: WeatherTarget; ramp: number; day: number; frozen?: boolean }
  | { t: "interactFx"; note: string }
  // Fishing (Living Waters, owner-only): the encounter edges — `nibble` (a tug;
  // pulling now never hooks), `bite` (arms the client's grab-window sweep — the
  // server runs its own authoritative window), `fishOn` (hooked: both sides
  // expand the SAME reel pattern from `seed`), `fishLost` (the fish left; the
  // CAST stays live), and the terminal `catch`/`escape` ("escaped" = lost the
  // reel fight; "invalid" = rejected cast). Nets: a full personalized sync on
  // join + add/remove deltas. `catch` carries the whole haul (one fish for
  // angling, the bulk roll for a net) — the client shows the keep/throw-back
  // preview and answers `keepCatch`.
  | { t: "nibble" }
  | { t: "bite" }
  | { t: "fishOn"; seed: number; fish: ItemType }
  | { t: "fishLost"; why: "spooked" | "interest" }
  | { t: "catch"; items: ItemType[]; kind: "angling" | "netting" }
  | { t: "escape"; reason?: "invalid" | "escaped" }
  | { t: "nets"; nets: NetDeploy[] }
  | { t: "net"; op: "add" | "remove"; net?: NetDeploy; id?: string }
  // Hunting traps (plan 11): a full personalized sync on join + add/remove
  // deltas — the nets pattern on land. A collected haul lands via the ordinary
  // `inventory` echo (no keep/throw preview — bulk is Trapping's identity).
  | { t: "traps"; traps: TrapDeploy[] }
  | { t: "trap"; op: "add" | "remove"; trap?: TrapDeploy; id?: string }
  | { t: "brews"; brews: BrewDeploy[] }
  | { t: "brew"; op: "add" | "remove"; brew?: BrewDeploy; id?: string }
  // Campfires (plan 25): a full personalized sync on join/transfer + add/remove
  // deltas — the brews pattern; `remove` also fires on burn-out (the tick sweep).
  | { t: "fires"; fires: FireDeploy[] }
  | { t: "fire"; op: "add" | "remove"; fire?: FireDeploy; id?: string }
  // Cooking (plan 25, owner-only): `panOn` starts the pan minigame — both sides
  // expand the SAME prompt pattern from `seed` (the fishOn twin); `cookResult`
  // resolves the cook — `cooked` bags the dish (also echoed via `inventory`),
  // `burnt` mints the Burnt Mess (item rides along), `invalid` = the server
  // rejected/cancelled the session (client backs out quietly).
  | { t: "panOn"; seed: number; type: ItemType }
  | { t: "cookResult"; outcome: "cooked" | "burnt" | "invalid"; item?: ItemType }
  // Tree-cutting (owner-only session + public prop cut state):
  // `chopOn` starts the minigame (both sides expand the same pattern from seed);
  // `chopResult` resolves — cut = stump+logs, failed = unlocked, invalid = cancelled,
  // exhausted = winded (begin rejected or desync force-end). Optional `sta` is the
  // server's attack-gate pool so the client HUD can re-sync (stamina is otherwise
  // predicted locally and can drift, e.g. after shield soaks).
  | { t: "chopOn"; id: string; seed: number }
  | { t: "chopResult"; outcome: "cut" | "failed" | "invalid" | "exhausted"; logs?: ItemType; count?: number; sta?: number }
  // Firestarting (owner-only session): `fireOn` starts the spark minigame at
  // (x,z); both sides expand the same pattern from seed. `fireResult` resolves
  // — lit = campfire spawned + materials spent, failed = embers died (no spend),
  // invalid = cancelled / torn down. Cost is materials only (no stamina).
  | { t: "fireOn"; seed: number; x: number; z: number }
  | { t: "fireResult"; outcome: "lit" | "failed" | "invalid" }
  | ImStats; // { t: "imstats"; ... } — the debug feed

/** Serialize a server→client message, TYPE-CHECKED against ServerMsg — the one
 *  way server code should build an outbound frame (board Q1: a renamed wire
 *  field now fails to compile at the send site instead of silently drifting).
 *  The two exceptions, deliberately hand-assembled for perf: the per-tick
 *  `state` snapshot (fragment concat in interest.ts) and the entity fragments
 *  themselves (broadcast.ts) — their shapes are pinned by NetPlayer/NetNpc/
 *  WorldItem instead. */
export function packMsg(m: ServerMsg): string {
  return JSON.stringify(m);
}
